// src/components/route-optimization/RouteAnalyzer.tsx
import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  Box,
  Typography,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Select,
  MenuItem,
  Button,
  Alert,
  Snackbar,
  ListSubheader,
  TextField,
  CircularProgress,
  InputAdornment,
  Tooltip,
} from "@mui/material";
import WorldRouteMap from "./WorldRouteMap";
import { apiService } from "../../services/api";
import type {
  RiskBreakdownResponse,
  RiskRecommendationsResponse,
} from "./RouteRiskBreakdown";
import SelectedScenarioPanel from "./SelectedScenarioPanel";
import { LegendSwatch, RouteCard } from "./RouteAnalyzerCards";
import type { SavedScenario } from "./RouteOptimizationTabs";
import { TRANSPORT_COLORS, TRANSPORT_LINE_STYLES } from "./transportTheme";
import {
  buildRouteCardViewModel,
  buildSavedScenario,
  type CapacityCheckResponse,
  type FeasibleSupplier,
  getNoFeasibleSupplierMessage,
  getRouteModeLabels,
  hasRoutes,
  type LocationNode,
  mergeCapacityRoutes,
  shouldDisplayRiskInsights,
  sortRoutesForOptimization,
  syncSelectedSupplier,
  TEXT_SECONDARY,
  WL_RED,
} from "./routeAnalyzerShared";

const WL_BLACK = "#1E1E1E";

interface RouteAnalyzerProps {
  onSaveScenario?: (
    scenario: SavedScenario,
  ) => void | Promise<void> | Promise<{ overwritten: boolean } | void>;
  selectedScenario?: SavedScenario | null;
  tutorialExample?: Record<string, string>;
  tutorialStepId?: string;
}

const RouteAnalyzer: React.FC<RouteAnalyzerProps> = ({ onSaveScenario, selectedScenario, tutorialExample, tutorialStepId }) => {
  /* -------------------- State -------------------- */
  const [product, setProduct] = useState("All Products");
  const [products, setProducts] = useState<string[]>(["All Products"]);

  const [destination, setDestination] = useState("");
  const [destinations, setDestinations] = useState<LocationNode[]>([]);

  const [qty, setQty] = useState<string>("");

  // Supplier dropdown – dynamically filtered by feasibility
  const [supplier, setSupplier] = useState("");
  const [feasibleSuppliers, setFeasibleSuppliers] = useState<FeasibleSupplier[]>([]);
  const [suppliersLoading, setSuppliersLoading] = useState(false);
  const [noFeasibleWarning, setNoFeasibleWarning] = useState(false);
  const [noFeasibleWarningMessage, setNoFeasibleWarningMessage] = useState<React.ReactNode>("");
  // Show the mandatory-fields warning only after the user attempts to submit
  const [showMandatoryFieldsWarning, setShowMandatoryFieldsWarning] = useState(false);

  const [selectedStrategy, setSelectedStrategy] = useState<string>("");
  const optimization = selectedStrategy;

  const [routes, setRoutes] = useState<any[]>([]);
  const [allRouteNodes, setAllRouteNodes] = useState<any[]>([]);
  const [mapNodes, setMapNodes] = useState<any[]>([]);
  const [selectedRouteIndex, setSelectedRouteIndex] = useState(0);
  const [selectedRouteId, setSelectedRouteId] = useState<string | null>(null);
  const [hoveredRouteId, setHoveredRouteId] = useState<string | null>(null);
  const [expandedRouteIds, setExpandedRouteIds] = useState<Set<number>>(new Set());

  // Selected Scenario panel
  // Avoid 'any | null' (where 'any' overrides the union). Keep a single 'any' type
  // so nullable initial state is represented by the runtime value instead of a
  // redundant union type.
  const [panelRoute, setPanelRoute] = useState<any>(null);
  const [panelRouteIdx, setPanelRouteIdx] = useState<number>(0);
  const [orderDate, setOrderDate] = useState<string>("");
  const [expandedRiskScore, setExpandedRiskScore] = useState<boolean>(false);
  const [expandedTariffBreakdown, setExpandedTariffBreakdown] = useState<boolean>(false);
  const [panelRiskBreakdown, setPanelRiskBreakdown] = useState<RiskBreakdownResponse | null>(null);
  const [panelRiskBreakdownLoading, setPanelRiskBreakdownLoading] = useState<boolean>(false);
  const [panelRecommendations, setPanelRecommendations] = useState<RiskRecommendationsResponse | null>(null);
  const [panelRecommendationsLoading, setPanelRecommendationsLoading] = useState<boolean>(false);
  const [showRiskGraph, setShowRiskGraph] = useState<boolean>(true);

  const onToggleRiskView = useCallback(() => {
    setShowRiskGraph((prev) => !prev);
  }, []);

  // When a route is opened in the side panel, load the supporting risk analytics
  // in parallel so the drawer can show both breakdown and recommendation data.
  useEffect(() => {
    let cancelled = false;

    if (!panelRoute) {
      setPanelRiskBreakdown(null);
      setPanelRecommendations(null);
      return;
    }
    if (!shouldDisplayRiskInsights(panelRoute)) {
      setPanelRiskBreakdown(null);
      setPanelRecommendations(null);
      setPanelRiskBreakdownLoading(false);
      setPanelRecommendationsLoading(false);
      setShowRiskGraph(true);
      return;
    }
    const shipmentId = panelRoute.shipment_id ? String(panelRoute.shipment_id) : undefined;
    const routeId = panelRoute.route_id ? String(panelRoute.route_id) : undefined;
    if (!shipmentId && !routeId) {
      setPanelRiskBreakdown(null);
      setPanelRecommendations(null);
      return;
    }
    setPanelRiskBreakdownLoading(true);
    setPanelRecommendationsLoading(true);
    const loadPanelInsights = async () => {
      const riskInsightParams = {
        ...(shipmentId ? { shipment_id: shipmentId } : {}),
        ...(routeId ? { route_id: routeId } : {}),
      };

      const [breakdownRes, recommendationsRes] = await Promise.allSettled([
        apiService.getRiskBreakdown(riskInsightParams),
        apiService.getRiskRecommendations(riskInsightParams),
      ]);

      if (cancelled) {
        return;
      }

      if (breakdownRes.status === "fulfilled") {
        const data = breakdownRes.value?.data ?? breakdownRes.value;
        setPanelRiskBreakdown(data);
      } else {
        setPanelRiskBreakdown(null);
      }

      if (recommendationsRes.status === "fulfilled") {
        const data = recommendationsRes.value?.data ?? recommendationsRes.value;
        setPanelRecommendations(data);
      } else {
        setPanelRecommendations(null);
      }

      setShowRiskGraph(true);

      setPanelRiskBreakdownLoading(false);
      setPanelRecommendationsLoading(false);
    };

    loadPanelInsights().catch(() => {
      if (cancelled) {
        return;
      }
      setPanelRiskBreakdown(null);
      setPanelRecommendations(null);
      setPanelRiskBreakdownLoading(false);
      setPanelRecommendationsLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [panelRoute]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saveSnackbarOpen, setSaveSnackbarOpen] = useState(false);
  const [saveSnackbarMessage, setSaveSnackbarMessage] = useState("Scenario saved.");

  // "Search committed" state: locks all filter controls after optimization
  const [isSearchCommitted, setIsSearchCommitted] = useState(false);

  // Capacity check result for displaying infeasible warnings
  const [capacityResult, setCapacityResult] = useState<CapacityCheckResponse | null>(null);
  const productFormRef = useRef<HTMLDivElement | null>(null);
  const destinationFormRef = useRef<HTMLDivElement | null>(null);
  const supplierFormRef = useRef<HTMLDivElement | null>(null);
  const [productMenuWidth, setProductMenuWidth] = useState<number | undefined>();
  const [destinationMenuWidth, setDestinationMenuWidth] = useState<number | undefined>();
  const [supplierMenuWidth, setSupplierMenuWidth] = useState<number | undefined>();
  const feasibleSupplierRequestRef = useRef(0);
  const loadingSavedScenarioRef = useRef(false);
  const savedScenarioSupplierRef = useRef("");
  const autoLoadSavedScenarioIdRef = useRef<string | null>(null);
  const tutorialValuesRef = useRef<Record<string, string> | null>(null);

  /* -------------------- Derived flags for sequential enablement -------------------- */
  const isProductSelected = !!product && product !== "All Products";
  const isQtyEntered = !!qty && Number.parseInt(qty, 10) > 0;
  const isSupplierReady = feasibleSuppliers.length > 0;

  /* -------------------- Load products on mount -------------------- */
  useEffect(() => {
    const loadRouteProducts = async () => {
      try {
        const res = await apiService.getProducts();
        const productList: string[] = Array.isArray(res.data) ? res.data : [];

        if (productList.length > 0) {
          setProducts(["All Products", ...productList]);
          return;
        }

      

        setError("No route products available.");
      } catch (err) {
        console.error("Failed to load route products:", err);
        setError("Failed to load route products.");
      }
    };

    loadRouteProducts();
  }, []);

  /* -------------------- On product change: fetch unit weight + destinations -------------------- */
  useEffect(() => {
    if (loadingSavedScenarioRef.current) {
      if (!isProductSelected) return;
      apiService.getDestinations(product)
        .then(res => setDestinations(res.data || []))
        .catch(err => {
          console.error("Failed to load destinations:", err);
        });
      return;
    }

    const pendingTutorial = tutorialValuesRef.current;
    const isTutorialProductChange = pendingTutorial?.product === product;

    if (!isTutorialProductChange) {
      // Reset downstream state
      setDestination("");
      setQty("");
      setSupplier("");
      setFeasibleSuppliers([]);
      setNoFeasibleWarning(false);
      setNoFeasibleWarningMessage("");
      setShowMandatoryFieldsWarning(false);
      clearRouteState();
    }
    setDestinations([]);

    if (!isProductSelected) return;



    // Fetch destinations for this product
    apiService.getDestinations(product)
      .then(res => {
        const dests: LocationNode[] = res.data || [];
        setDestinations(dests);
        if (!isTutorialProductChange) return;
        // Re-apply tutorial values now that destinations are loaded
        if (pendingTutorial.destination) {
          const pendingDest = pendingTutorial.destination.toLowerCase();
          const match = dests.find(
            (d) =>
              d.node_name.toLowerCase() === pendingDest ||
              d.node_name.toLowerCase().includes(pendingDest) ||
              d.country.toLowerCase() === pendingDest
          );
          setDestination(match ? match.node_name : pendingTutorial.destination);
        }
        if (pendingTutorial.quantity !== undefined) setQty(pendingTutorial.quantity);
        if (pendingTutorial.strategy) setSelectedStrategy(pendingTutorial.strategy);
        tutorialValuesRef.current = null;
      })
      .catch(err => {
        console.error("Failed to load destinations:", err);
      });
  }, [product]); // eslint-disable-line react-hooks/exhaustive-deps

  /* -------------------- On destination change: reset qty + supplier -------------------- */
  useEffect(() => {
    if (loadingSavedScenarioRef.current) {
      if (isProductSelected && destination) {
        fetchFeasibleSuppliers();
      }
      loadingSavedScenarioRef.current = false;
      return;
    }

    setSupplier("");
    setFeasibleSuppliers([]);
    setNoFeasibleWarning(false);
    // Clear warning states so the UI doesn't show stale alerts after changing upstream inputs.
    setShowMandatoryFieldsWarning(false);
    setNoFeasibleWarningMessage("");
    clearRouteState();

    // Immediately fetch suppliers for the new destination (without qty filter)
    if (isProductSelected && destination) {
      fetchFeasibleSuppliers();
    }
  }, [destination]); // eslint-disable-line react-hooks/exhaustive-deps

  /* -------------------- On qty or destination change: fetch suppliers -------------------- */
  const fetchFeasibleSuppliers = useCallback(async () => {
    const requestId = ++feasibleSupplierRequestRef.current;
    const qtyNum = Number.parseInt(qty, 10);
    const hasQtyFilter = Number.isInteger(qtyNum) && qtyNum > 0;
    const selectedSupplier = supplier;
    const savedScenarioSupplier = savedScenarioSupplierRef.current;

    if (!isProductSelected || !destination) {
      setFeasibleSuppliers([]);
      setNoFeasibleWarning(false);
      setNoFeasibleWarningMessage("");
      setSupplier("");
      return;
    }

    setSuppliersLoading(true);
    setNoFeasibleWarning(false);
    setNoFeasibleWarningMessage("");

    try {
      // First query uses the entered quantity when available so the supplier
      // dropdown only shows suppliers that can actually satisfy the request.
      const res = await apiService.getFeasibleSuppliers({
        product_name: product,
        qty: hasQtyFilter ? qtyNum : undefined,
        destination: destination || undefined,
      });
      const suppliers: FeasibleSupplier[] = res.data?.suppliers ?? [];
      if (requestId !== feasibleSupplierRequestRef.current) return;
      setFeasibleSuppliers(suppliers);
      syncSelectedSupplier(
        suppliers,
        selectedSupplier,
        savedScenarioSupplier,
        setSupplier,
        savedScenarioSupplierRef,
      );
      if (hasQtyFilter && suppliers.length === 0) {
        // If nothing matches the requested quantity, fetch the broader supplier
        // set to build a more specific warning about min/max quantity limits.
        const allSuppliersRes = await apiService.getFeasibleSuppliers({
          product_name: product,
          destination: destination || undefined,
        });
        const allSuppliers: FeasibleSupplier[] = allSuppliersRes.data?.suppliers ?? [];
        if (requestId !== feasibleSupplierRequestRef.current) return;
        setNoFeasibleWarningMessage(
          getNoFeasibleSupplierMessage(qtyNum, allSuppliers, optimization),
        );
        setNoFeasibleWarning(true);
      } else {
        setNoFeasibleWarningMessage("");
        setNoFeasibleWarning(false);
      }
    } catch (err) {
      if (requestId !== feasibleSupplierRequestRef.current) return;
      console.error("Failed to fetch feasible suppliers:", err);
      setFeasibleSuppliers([]);
      setSupplier("");
      setNoFeasibleWarning(true);
      setNoFeasibleWarningMessage("Failed to evaluate supplier quantity limits.");
    } finally {
      setSuppliersLoading(false);
    }
  }, [product, qty, destination, isProductSelected, optimization, supplier]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isProductSelected || !destination) {
      setFeasibleSuppliers([]);
      setNoFeasibleWarning(false);
      setNoFeasibleWarningMessage("");
      return;
    }

    // Debounce supplier lookups while the user is still typing quantity values.
    const timer = setTimeout(() => {
      fetchFeasibleSuppliers();
    }, 400);

    return () => clearTimeout(timer);
  }, [qty, destination, fetchFeasibleSuppliers, isProductSelected]);

  /* -------------------- Node country lookup -------------------- */
  const allNodeLookup = useMemo(
    () => new Map(allRouteNodes.map((n: any) => [n.node_name as string, n])),
    [allRouteNodes]
  );

  const displayRoutes = useMemo(
    () => sortRoutesForOptimization(routes, optimization),
    [routes, optimization],
  );

  const resolvedSelectedRouteIndex = useMemo(() => {
    if (selectedRouteId) {
      const idx = displayRoutes.findIndex((r) => r?.route_id === selectedRouteId);
      if (idx >= 0) return idx;
    }
    return Math.min(selectedRouteIndex, Math.max(displayRoutes.length - 1, 0));
  }, [displayRoutes, selectedRouteId, selectedRouteIndex]);

  const buildMapNodesForRoute = useCallback((route: any, allNodes: any[]) => {
    if (!route?.nodes) return [];
    const nodeMap = new Map(
      allNodes.map((n: any) => [n.node_name, n])
    );
    return route.nodes
      .map((name: string) => nodeMap.get(name))
      .filter(Boolean);
  }, []);


  /* -------------------- Map node tracking -------------------- */
  useEffect(() => {
    if (!displayRoutes.length || !allRouteNodes.length) return;
    const selected = displayRoutes[resolvedSelectedRouteIndex];
    if (!selected) return;
    setMapNodes(buildMapNodesForRoute(selected, allRouteNodes));
  }, [resolvedSelectedRouteIndex, displayRoutes, allRouteNodes, buildMapNodesForRoute]);

  /* -------------------- Reset state -------------------- */
  // Clear every route-driven UI surface so upstream filter changes do not leave
  // stale map selections, banners, or side-panel state behind.
  const clearRouteState = () => {
    setRoutes([]);
    setAllRouteNodes([]);
    setMapNodes([]);
    setSelectedRouteIndex(0);
    setSelectedRouteId(null);
    setHoveredRouteId(null);
    setCapacityResult(null);
    setSaveSnackbarOpen(false);
    setExpandedRouteIds(new Set());
    setIsSearchCommitted(false);
  };

  useEffect(() => {
    // Rehydrate the builder from a saved scenario, then let the normal effects
    // reload destinations and supplier feasibility for the restored filters.
    if (!selectedScenario) return;

    autoLoadSavedScenarioIdRef.current = selectedScenario.id;
    loadingSavedScenarioRef.current = true;
    setProduct(selectedScenario.product || "All Products");
    setDestination(selectedScenario.destination || "");
    setQty(selectedScenario.quantity || "");
    savedScenarioSupplierRef.current = selectedScenario.supplier || "";
    setSupplier("");
    setSelectedStrategy(selectedScenario.strategy || "");
    setDestinations([]);
    setFeasibleSuppliers([]);
    setNoFeasibleWarning(false);
    setNoFeasibleWarningMessage("");
    setShowMandatoryFieldsWarning(false);
    setError(null);
    setIsSearchCommitted(false);
    clearRouteState();
  }, [selectedScenario]);

  useEffect(() => {
    if (!tutorialExample || Object.keys(tutorialExample).length === 0) return;
    tutorialValuesRef.current = { ...tutorialExample };
    if (tutorialExample.product) setProduct(tutorialExample.product);
    if (tutorialExample.destination) setDestination(tutorialExample.destination);
    if (tutorialExample.quantity !== undefined) setQty(tutorialExample.quantity);
    if (tutorialExample.strategy) setSelectedStrategy(tutorialExample.strategy);
  }, [tutorialExample]);

  useEffect(() => {
    if (tutorialStepId !== "route-apply") return;
    if (!isProductSelected || !destination || !selectedStrategy) return;
    if (isSearchCommitted || loading) return;
    handleGetRoute();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tutorialStepId, isProductSelected, destination, selectedStrategy, isSearchCommitted, loading]);

  // When the tutorial reaches the results step, auto-open the first route so
  // the details panel is visible for reviewing cost, risk, and tariff breakdowns.
  useEffect(() => {
    if (tutorialStepId !== "route-selected-scenario") return;
    if (displayRoutes.length === 0) return;
    if (panelRoute) return;
    handleSelectRouteCard(displayRoutes[0], 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tutorialStepId, displayRoutes, panelRoute]);

  const handleFullReset = () => {
    // Reset top-level filters to initial defaults
    setProduct("All Products");
    setDestination("");
    setQty("");
    setSupplier("");
    setSelectedStrategy("");

    // Clear dependent dropdown data and flags
    setDestinations([]);
    setFeasibleSuppliers([]);
    setNoFeasibleWarning(false);
    setNoFeasibleWarningMessage("");

    // Clear route and UI state
    setIsSearchCommitted(false);
    setError(null);
    setShowMandatoryFieldsWarning(false);
    clearRouteState();
  };

  /* -------------------- Group locations by country -------------------- */
  const groupByCountry = (nodes: LocationNode[]) => {
    const grouped: { [country: string]: LocationNode[] } = {};
    nodes.forEach((node) => {
      const country = node.country || "Other";
      if (!grouped[country]) {
        grouped[country] = [];
      }
      grouped[country].push(node);
    });
    const sortedCountries = Object.keys(grouped).sort((a, b) => a.localeCompare(b));
    return sortedCountries.reduce((acc, country) => {
      acc[country] = [...grouped[country]].sort((a: LocationNode, b: LocationNode) =>
        a.node_name.localeCompare(b.node_name)
      );
      return acc;
    }, {} as { [country: string]: LocationNode[] });
  };

  const handleRouteSelectByIndex = useCallback((index: number) => {
    setSelectedRouteIndex(index);
    const route = displayRoutes[index];
    if (route) {
      setSelectedRouteId(route.route_id || null);
      setMapNodes(buildMapNodesForRoute(route, allRouteNodes));
    }
  }, [allRouteNodes, buildMapNodesForRoute, displayRoutes]);

  const handleRouteSelectById = useCallback((routeId: string) => {
    const index = displayRoutes.findIndex((r) => r.route_id === routeId);
    if (index >= 0) {
      handleRouteSelectByIndex(index);
    }
  }, [handleRouteSelectByIndex, displayRoutes]);

  const saveScenarioSnapshot = useCallback(async (route?: any) => {
    if (!onSaveScenario) return;
    const result = await Promise.resolve(
      onSaveScenario(
        buildSavedScenario({
          product,
          destination,
          qty,
          supplier,
          optimization,
          route,
        })
      )
    );
    const overwritten = Boolean(
      result && typeof result === "object" && "overwritten" in result && result.overwritten,
    );
    setSaveSnackbarMessage(overwritten ? "Scenario updated." : "Scenario saved.");
    setSaveSnackbarOpen(true);
  }, [destination, onSaveScenario, optimization, product, qty, supplier]);

  const handleSaveSelectedScenario = useCallback(() => {
    const selectedRoute = routes[selectedRouteIndex] ?? routes[0];
    saveScenarioSnapshot(selectedRoute);
  }, [routes, selectedRouteIndex, saveScenarioSnapshot]);

  const toggleExpandedRoute = useCallback((routeIdx: number) => {
    setExpandedRouteIds((prev) => {
      const next = new Set(prev);
      if (next.has(routeIdx)) {
        next.delete(routeIdx);
      } else {
        next.add(routeIdx);
      }
      return next;
    });
  }, []);

  const handleSelectRouteCard = useCallback((selectedRoute: any, routeIdx: number) => {
    handleRouteSelectByIndex(routeIdx);
    setPanelRoute(selectedRoute);
    setPanelRouteIdx(routeIdx);
    setOrderDate("");
    setExpandedRiskScore(false);
    setShowRiskGraph(true);
  }, [handleRouteSelectByIndex]);
    /* -------------------- Get Route (optimize) -------------------- */
  const handleGetRoute = useCallback(async () => {
    if (!isProductSelected) {
      setShowMandatoryFieldsWarning(true);
      return;
    }
    if (!destination) {
      setShowMandatoryFieldsWarning(true);
      return;
    }
    if (!selectedStrategy) {
      setShowMandatoryFieldsWarning(true);
      return;
    }

    setError(null);
    setLoading(true);
    setRoutes([]);
    setMapNodes([]);
    setSelectedRouteIndex(0);
    setSelectedRouteId(null);
    setHoveredRouteId(null);
    setCapacityResult(null);

    const qtyNum = Number.parseInt(qty, 10);

    const handleCapacityFlow = async (qty: number) => {
      const capRes = await apiService.checkCapacity({
        product_name: product,
        qty,
        opt: optimization,
        supplier_name: supplier?.trim() || undefined,
        destination: destination || undefined,
      });

      const data: CapacityCheckResponse = capRes.data;
      const capacityRoutes = hasRoutes(data.routes) ? data.routes : [];

      if (!data.feasible) {
        setCapacityResult(data);
        if (!capacityRoutes.length) {
          setIsSearchCommitted(true);
          return { done: true };
        }
      }

      if (!capacityRoutes.length) {
        return { done: false };
      }

      setCapacityResult(data);

      const setFallbackRoutes = async () => {
        const fallbackRoutes = sortRoutesForOptimization(capacityRoutes, optimization);
        const bestFallback = fallbackRoutes[0];
        setRoutes(fallbackRoutes);
        setSelectedRouteId(bestFallback?.route_id || null);
      };

      try {
        const routeRes = await apiService.getRoute({
          product_name: product,
          supplier_name: supplier?.trim() || undefined,
          destination_location: destination,
          opt: optimization,
          qty,
        });
        const { routes: fetchedRoutes, nodes: allNodes } = routeRes.data;
        setAllRouteNodes(allNodes || []);

        if (hasRoutes(fetchedRoutes)) {
          const mergedRoutes = mergeCapacityRoutes(capacityRoutes, fetchedRoutes);
          const sortedRoutes = sortRoutesForOptimization(mergedRoutes, optimization);
          const best = sortedRoutes[0];

          setRoutes(sortedRoutes);
          setSelectedRouteId(best.route_id || null);
          setMapNodes(buildMapNodesForRoute(best, allNodes));
        } else {
          await setFallbackRoutes();
        }
      } catch {
        await setFallbackRoutes();
      }

      setIsSearchCommitted(true);
      return { done: true };
    };
    setSaveSnackbarOpen(false);

    try {
      if (qtyNum > 0) {
        const result = await handleCapacityFlow(qtyNum);
        if (result.done) {
          setLoading(false);
          return;
        }
      }

      const baseParams = {
        product_name: product,
        supplier_name: supplier?.trim() || undefined,
        destination_location: destination,
      };

      const res = await apiService.getRoute({ ...baseParams, opt: optimization });
      const { routes: fetchedRoutes, nodes: allNodes } = res.data;
      setAllRouteNodes(allNodes || []);

      if (!fetchedRoutes || fetchedRoutes.length === 0) {
        throw new Error("No routes returned");
      }

      const sortedFetchedRoutes = sortRoutesForOptimization(fetchedRoutes, optimization);
      setRoutes(sortedFetchedRoutes);
      const best = sortedFetchedRoutes[0];
      setSelectedRouteId(best.route_id || null);
      setMapNodes(buildMapNodesForRoute(best, allNodes));
      setIsSearchCommitted(true);
    } catch (err) {
      console.error("Route fetch failed:", err);
      setError("No route found. The selected locations may not be connected.");
    } finally {
      setLoading(false);
    }
  }, [
    buildMapNodesForRoute,
    destination,
    isProductSelected,
    optimization,
    product,
    qty,
    selectedStrategy,
    supplier,
  ]);

  useEffect(() => {
    const pendingScenarioId = autoLoadSavedScenarioIdRef.current;
    if (!pendingScenarioId || !selectedScenario || pendingScenarioId !== selectedScenario.id) {
      return;
    }
    if (loadingSavedScenarioRef.current) {
      return;
    }
    if (!isProductSelected || !destination || !selectedStrategy) {
      return;
    }

    const savedSupplier = savedScenarioSupplierRef.current;
    if (savedSupplier && supplier !== savedSupplier) {
      return;
    }

    autoLoadSavedScenarioIdRef.current = null;
    handleGetRoute().catch((err) => {
      console.error("Failed to auto-load saved scenario route:", err);
    });
  }, [
    destination,
    handleGetRoute,
    isProductSelected,
    selectedScenario,
    selectedStrategy,
    supplier,
  ]);
  /* -------------------- Derived map props -------------------- */
  const resolvedMapNodes = (() => {
    if (loading) return [];
    if (mapNodes.length > 0) return mapNodes;
    // Compute nodes for the selected route synchronously to avoid the race
    // where allRouteNodes is set but mapNodes has not been updated yet.
    // Falling back to allRouteNodes would pass every node from every route
    // to the map, causing stray markers and a wrong fallback polyline.
    const selected = routes[selectedRouteIndex];
    if (selected) return buildMapNodesForRoute(selected, allRouteNodes);
    return [];
  })();

  const resolvedEdges = (() => {
    if (loading) return [];
    if (displayRoutes.length === 1) return displayRoutes[0]?.edges || [];
    return displayRoutes[resolvedSelectedRouteIndex]?.edges || [];
  })();

  /* -------------------- Route card helpers -------------------- */
  const routeHasModeLabel = useCallback(
    (route: any, modeLabel: string) => getRouteModeLabels(route).includes(modeLabel),
    [],
  );
  const canSaveScenario = Boolean(onSaveScenario) && isSearchCommitted && routes.length > 0;
  const primaryActionLabel = canSaveScenario ? "Save Scenario" : "Apply Filters";
  const handlePrimaryAction = canSaveScenario ? handleSaveSelectedScenario : handleGetRoute;

  useEffect(() => {
    if (tutorialStepId !== "route-save") return;
    if (!canSaveScenario || loading || !panelRoute) return;
    handleSaveSelectedScenario();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tutorialStepId, canSaveScenario, loading, panelRoute]);

  useEffect(() => {
    if (!tutorialStepId) return;
    const stepsWithPanel = ["route-selected-scenario"];
    if (!stepsWithPanel.includes(tutorialStepId) && panelRoute) {
      setPanelRoute(null);
    }
  }, [tutorialStepId, panelRoute]);

  /* -------------------- Render -------------------- */
  return (
    <Box sx={{ p: 0, width: "100%", overflow: "hidden" }}>

      {/* Route saved snackbar */}
      <Snackbar
        open={saveSnackbarOpen}
        autoHideDuration={3000}
        onClose={(_, reason) => {
          if (reason === "clickaway") return;
          setSaveSnackbarOpen(false);
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSaveSnackbarOpen(false)}
          severity="success"
          sx={{ width: "100%" }}
        >
          {saveSnackbarMessage}
        </Alert>
      </Snackbar>

      {/* Main workspace: builder controls on the left, route exploration on the right. */}
      {/* ── 2-column layout ── */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "300px 1fr" },
          gap: "24px",
          alignItems: "start",
          width: "100%",
          minWidth: 0,
        }}
      >
        {/* Scenario builder drives the API queries and is locked once a search is committed. */}
        {/* ── LEFT: Scenario Builder ── */}
        <Box
          sx={{
            backgroundColor: "#fff",
            border: "1px solid #EAEAEA",
            borderRadius: "12px",
            boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
            p: "20px",
          }}
        >
          {/* Header */}
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: "20px" }}>
            <Typography
              sx={{
                fontFamily: "'Inter', sans-serif",
                fontSize: "1rem",
                fontWeight: 600,
                color: "#000",
              }}
            >
              Scenario Builder
            </Typography>
            <Tooltip title="Reset scenario inputs" placement="top">
              <button
                onClick={handleFullReset}
                style={{
                  background: "none",
                  border: "1px solid #E5E7EB",
                  borderRadius: "8px",
                  cursor: "pointer",
                  padding: "6px 8px",
                  display: "flex",
                  alignItems: "center",
                  color: "#6B7280",
                }}
                aria-label="Reset scenario inputs"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
                  <path d="M3 3v5h5" />
                </svg>
              </button>
            </Tooltip>
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {/* Product * */}
            <Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Typography
                  sx={{
                    fontFamily: "'Inter', sans-serif",
                    fontSize: "0.75rem",
                    fontWeight: 500,
                    color: "#4B5563",
                    mb: "6px",
                  }}
                >
                  Product{" "}
                  <span style={{ color: "#EF4444" }}>*</span>
                </Typography>
              </Box>

              <FormControl fullWidth size="small" ref={productFormRef}>
                <Select
                  value={product === "All Products" ? "" : product}
                  onChange={(e) => {
                    setProduct(e.target.value || "All Products");
                    clearRouteState();
                  }}
                  onOpen={() =>
                    setProductMenuWidth(
                      productFormRef.current?.getBoundingClientRect().width,
                    )
                  }
                  displayEmpty
                  renderValue={(val) =>
                    val ? <span>{val as string}</span> : <span style={{ color: "#9CA3AF" }}>Select product...</span>
                  }
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        maxHeight: 300,
                        width: productMenuWidth,
                        maxWidth: productMenuWidth,
                      },
                    },
                  }}
                  sx={{
                    fontFamily: "'Inter', sans-serif",
                    fontSize: "0.875rem",
                    borderRadius: "8px",
                    "& .MuiOutlinedInput-notchedOutline": { borderColor: "#EAEAEA" },
                    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#9CA3AF" },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: WL_RED, borderWidth: "2px" },
                    "&.Mui-focused": { boxShadow: "0 0 0 3px rgba(239,45,62,0.12)" },
                  }}
                >
                  {products
                    .filter((p) => p !== "All Products")
                    .map((p) => (
                      <MenuItem key={p} value={p} sx={{ fontFamily: "'Inter', sans-serif" }}>
                        {p}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Box>

            {/* Destination * */}
            <Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Typography
                  sx={{
                    fontFamily: "'Inter', sans-serif",
                    fontSize: "0.75rem",
                    fontWeight: 500,
                    color: "#4B5563",
                    mb: "6px",
                  }}
                >
                  Destination{" "}
                  <span style={{ color: "#EF4444" }}>*</span>
                </Typography>
              </Box>
              <FormControl
                fullWidth
                size="small"
                disabled={!isProductSelected}
                ref={destinationFormRef}
              >
                <Select
                  value={destination}
                  onChange={(e) => {
                    setDestination(e.target.value);
                    clearRouteState();
                  }}
                  onOpen={() =>
                    setDestinationMenuWidth(
                      destinationFormRef.current?.getBoundingClientRect().width,
                    )
                  }
                  displayEmpty
                  renderValue={(val) =>
                    val ? <span>{val as string}</span> : <span style={{ color: "#9CA3AF" }}>Select destination...</span>
                  }
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        maxHeight: 300,
                        width: destinationMenuWidth,
                        maxWidth: destinationMenuWidth,
                      },
                    },
                  }}
                  sx={{
                    fontFamily: "'Inter', sans-serif",
                    fontSize: "0.875rem",
                    borderRadius: "8px",
                    "& .MuiOutlinedInput-notchedOutline": { borderColor: "#EAEAEA" },
                    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#9CA3AF" },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: WL_RED, borderWidth: "2px" },
                    "&.Mui-focused": { boxShadow: "0 0 0 3px rgba(239,45,62,0.12)" },
                  }}
                >
                  <MenuItem value="" disabled>
                    Select destination...
                  </MenuItem>
                  {Object.entries(groupByCountry(destinations)).flatMap(([country, cities]) => [
                    <ListSubheader key={country} sx={{ fontFamily: "'Inter', sans-serif", fontWeight: 600, color: WL_BLACK }}>
                      {country}
                    </ListSubheader>,
                    ...cities.map((city) => (
                      <MenuItem
                        key={`${city.node_name}-${country}`}
                        value={city.node_name}
                        sx={{ pl: 4, fontFamily: "'Inter', sans-serif" }}
                      >
                        {city.node_name}
                      </MenuItem>
                    )),
                  ])}
                </Select>
              </FormControl>
            </Box>

            {/* Quantity */}
            <Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Typography
                  sx={{
                    fontFamily: "'Inter', sans-serif",
                    fontSize: "0.75rem",
                    fontWeight: 500,
                    color: "#4B5563",
                    mb: "6px",
                  }}
                >
                  Quantity
                </Typography>
              </Box>
              <TextField
                fullWidth
                size="small"
                type="text"
                value={qty}
                onChange={(e) => {
                  setQty(e.target.value.replaceAll(/\D/g, ""));
                  clearRouteState();
                }}
                placeholder="Enter quantity"
                disabled={!isProductSelected}
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <span style={{ fontFamily: "'Inter', sans-serif", fontSize: "0.8125rem", color: "#6B7280" }}>
                          units
                        </span>
                      </InputAdornment>
                    ),
                  },
                  htmlInput: { inputMode: "numeric", pattern: "[0-9]*" },
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    fontFamily: "'Inter', sans-serif",
                    fontSize: "0.875rem",
                    borderRadius: "8px",
                    "& fieldset": { borderColor: "#EAEAEA" },
                    "&:hover fieldset": { borderColor: "#9CA3AF" },
                    "&.Mui-focused fieldset": { borderColor: WL_RED, borderWidth: "2px" },
                    "&.Mui-focused": { boxShadow: "0 0 0 3px rgba(239,45,62,0.12)" },
                  },
                  "& input::placeholder": { color: "#9CA3AF", opacity: 1 },
                }}
              />
            </Box>

            {/* Supplier (optional) */}
            <Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Typography
                  sx={{
                    fontFamily: "'Inter', sans-serif",
                    fontSize: "0.75rem",
                    fontWeight: 500,
                    color: "#4B5563",
                    mb: "6px",
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                  }}
                >
                  Supplier{" "}
                  <span style={{ color: "#6B7280", fontWeight: 400, fontSize: "0.875rem" }}>(optional)</span>
                  {suppliersLoading && <CircularProgress size={12} sx={{ ml: 0.5 }} />}
                </Typography>
              </Box>
              <FormControl
                fullWidth
                size="small"
                disabled={!isSupplierReady || suppliersLoading}
                ref={supplierFormRef}
              >
                <Select
                  value={supplier}
                  onChange={(e) => {
                    setSupplier(e.target.value);
                    clearRouteState();
                  }}
                  onOpen={() =>
                    setSupplierMenuWidth(
                      supplierFormRef.current?.getBoundingClientRect().width,
                    )
                  }
                  displayEmpty
                  renderValue={(val) =>
                    val ? <span>{val as string}</span> : <span style={{ color: "#9CA3AF" }}>Select supplier...</span>
                  }
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        maxHeight: 300,
                        width: supplierMenuWidth,
                        maxWidth: supplierMenuWidth,
                      },
                    },
                  }}
                  sx={{
                    fontFamily: "'Inter', sans-serif",
                    fontSize: "0.875rem",
                    borderRadius: "8px",
                    "& .MuiOutlinedInput-notchedOutline": { borderColor: "#EAEAEA" },
                    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#9CA3AF" },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: WL_RED, borderWidth: "2px" },
                    "&.Mui-focused": { boxShadow: "0 0 0 3px rgba(239,45,62,0.12)" },
                  }}
                >
                  <MenuItem value="" sx={{ fontFamily: "'Inter', sans-serif", color: "#6B7280" }}>
                    Any supplier
                  </MenuItem>
                  {feasibleSuppliers.map((s) => (
                    <MenuItem
                      key={s.supplier_name}
                      value={s.supplier_name}
                      sx={{
                        fontFamily: "'Inter', sans-serif",
                        whiteSpace: "normal",
                        alignItems: "flex-start",
                        flexDirection: "column",
                        gap: 0.25,
                      }}
                    >
                      <Box component="span" sx={{ fontSize: "0.875rem", color: "#111827" }}>
                        {s.supplier_name}
                      </Box>
                      <Box component="span" sx={{ fontSize: "0.75rem", color: "#6B7280" }}>
                        min: {s.supplier_min_qty ?? 0} units, max: {s.supplier_capacity} units
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Optimization Strategy * */}
            <Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Typography
                  sx={{
                    fontFamily: "'Inter', sans-serif",
                    fontSize: "0.75rem",
                    fontWeight: 500,
                    color: "#4B5563",
                    mb: "6px",
                  }}
                >
                  Optimization Strategy{" "}
                  <span style={{ color: "#EF4444" }}>*</span>
                </Typography>
              </Box>
              <Typography
                sx={{
                  fontFamily: "'Inter', sans-serif",
                  fontSize: "0.75rem",
                  color: "#6B7280",
                  mb: "8px",
                }}
              >
                Select only one option.
              </Typography>
              <RadioGroup
                value={selectedStrategy}
                onChange={(event) => {
                  setSelectedStrategy(event.target.value);
                  clearRouteState();
                }}
              >
                {(
                  [
                    { value: "cost", label: "Cost" },
                    { value: "distance", label: "Distance" },
                    { value: "risk", label: "Risk" },
                    { value: "time", label: "Time" },
                  ] as const
                ).map((strategy) => (
                  <FormControlLabel
                    key={strategy.value}
                    value={strategy.value}
                    control={
                      <Radio
                        size="small"
                        sx={{
                          p: 0.5,
                          mr: 0.75,
                          color: "#D1D5DB",
                          "&.Mui-checked": { color: WL_RED },
                        }}
                      />
                    }
                    label={strategy.label}
                    sx={{
                      m: 0,
                      "& .MuiFormControlLabel-label": {
                        fontFamily: "'Inter', sans-serif",
                        fontSize: "0.875rem",
                        color: selectedStrategy === strategy.value ? "#111111" : "#374151",
                        fontWeight: selectedStrategy === strategy.value ? 500 : 400,
                      },
                    }}
                  />
                ))}
              </RadioGroup>
            </Box>

            {/* Primary action button */}
            <Button
              data-tutorial-step="route-primary-action"
              variant="contained"
              fullWidth
              onClick={handlePrimaryAction}
              disabled={loading}
              sx={{
                fontFamily: "'Inter', sans-serif",
                fontWeight: 600,
                fontSize: "0.875rem",
                textTransform: "none",
                height: 44,
                borderRadius: "8px",
                backgroundColor: WL_RED,
                color: "#fff",
                mt: 0.5,
                "&:hover": { backgroundColor: "#D41F30" },
                "&:disabled": { backgroundColor: "#D1D5DB", color: "#9CA3AF" },
              }}
            >
              {loading ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : primaryActionLabel}
            </Button>


          </Box>
        </Box>

        {/* Results area combines global alerts, the map, and the ranked route list. */}
        {/* ── RIGHT: Route Map + Results ── */}
        <Box data-tutorial-step="route-results" sx={{ display: "flex", flexDirection: "column", gap: 3, minWidth: 0, overflow: "hidden" }}>
          {/* Error alert */}
          {error && (
            <Alert
              severity="error"
              onClose={() => setError(null)}
              sx={{ borderRadius: "8px", fontFamily: "'Inter', sans-serif" }}
            >
              {error}
            </Alert>
          )}

          {/* Mandatory fields warning */}
          {!loading && !isSearchCommitted && showMandatoryFieldsWarning && (!isProductSelected || !destination || !selectedStrategy) && (
            <Alert
              severity="error"
              sx={{ borderRadius: "8px", fontFamily: "'Inter', sans-serif", border: "1px solid #ff2f00", backgroundColor: "rgba(255, 47, 0, 0.08)" }}
            >
              Please fill all the mandatory fields to get the Optimal Route.
            </Alert>
          )}

          {noFeasibleWarning && isQtyEntered && !isSearchCommitted && (
            <Alert
              severity="error"
              sx={{
                borderRadius: "8px",
                fontFamily: "'Inter', sans-serif",
                boxShadow: "0 8px 24px rgba(15, 23, 42, 0.08)",
                backgroundColor: "rgba(255, 245, 245, 0.96)",
                py: 0.75,
                "& .MuiAlert-icon": {
                  mr: 0.75,
                  py: 0.25,
                },
                "& .MuiAlert-message": {
                  py: 0,
                  flex: 1,
                  minWidth: 0,
                  overflowWrap: "anywhere",
                  wordBreak: "break-word",
                  whiteSpace: "normal",
                  fontSize: "0.75rem",
                  lineHeight: 1.25,
                },
              }}
            >
              {noFeasibleWarningMessage || "No suppliers can fulfill this quantity. Please adjust the quantity or destination."}
            </Alert>
          )}
          {/* Single results card keeps the geographic view and route ranking visually tied together. */}
          {/* ── ONE outer white card: map + routes ── */}
          <Box
            sx={{
              backgroundColor: "#fff",
              border: "1px solid #EAEAEA",
              borderRadius: "12px",
              boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
              overflow: "hidden",
            }}
          >
            {/* Header bar */}
            <Box
              sx={{
                px: 2.5,
                py: 1.5,
                borderBottom: "1px solid #EAEAEA",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
                flexWrap: "wrap",
              }}
            >
              <Typography
                sx={{
                  fontFamily: "'Inter', sans-serif",
                  fontSize: "0.875rem",
                  fontWeight: 600,
                  color: "#000",
                }}
              >
                Route Map Overview
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap", justifyContent: "flex-end" }}>
                {/* Freight-mode legend — shown only when routes are loaded */}
                {!loading && routes.length > 0 && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                    {routes.some((r: any) => routeHasModeLabel(r, "Ocean")) && (
                      <LegendSwatch
                        color={TRANSPORT_COLORS.ocean}
                        dashArray={TRANSPORT_LINE_STYLES.Ocean.dashArray}
                        label="Ocean"
                      />
                    )}
                    {routes.some((r: any) => routeHasModeLabel(r, "Air")) && (
                      <LegendSwatch
                        color={TRANSPORT_COLORS.air}
                        dashArray={TRANSPORT_LINE_STYLES.Air.dashArray}
                        label="Air"
                      />
                    )}
                    {routes.some((r: any) => routeHasModeLabel(r, "Rail")) && (
                      <LegendSwatch
                        color={TRANSPORT_COLORS.ground}
                        dashArray={TRANSPORT_LINE_STYLES.Rail.dashArray}
                        label="Rail"
                      />
                    )}
                    {routes.some((r: any) => routeHasModeLabel(r, "Ground")) && (
                      <LegendSwatch
                        color={TRANSPORT_COLORS.ground}
                        dashArray={TRANSPORT_LINE_STYLES.Ground.dashArray}
                        label="Ground"
                      />
                    )}
                  </Box>
                )}
              </Box>
            </Box>

            {/* Map inner card — padded so map is not edge-to-edge */}
            <Box sx={{ px: 7, pt: 7, pb: 7 }}>
              <Box
                data-tutorial-step="route-map"
                sx={{
                  position: "relative",
                  height: 360,
                  backgroundColor: "#f0f4f8",
                  borderRadius: "10px",
                  overflow: "hidden",
                  border: "1px solid #EAEAEA",
                }}
              >

                {loading ? (
                  <Box
                    sx={{
                      height: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <CircularProgress size={32} sx={{ color: WL_RED }} />
                    <Typography
                      sx={{
                        ml: 2,
                        fontFamily: "'Inter', sans-serif",
                        fontSize: "0.875rem",
                        color: "#6B7280",
                      }}
                    >
                      Optimizing routes...
                    </Typography>
                  </Box>
                ) : (
                  <WorldRouteMap
                    nodes={resolvedMapNodes}
                    // The map intentionally renders only the actively selected
                    // route while the cards list the available alternatives.
                    edges={resolvedEdges}
                    selectedRouteId={selectedRouteId}
                    hoveredRouteId={hoveredRouteId}
                    onRouteClick={handleRouteSelectById}
                    onRouteHover={setHoveredRouteId}
                    showFallbackPolyline={true}
                  />
                )}
              </Box>
            </Box>
          {/* end map inner card */}

          {/* Infeasible state — inside the outer white card */}
          {!loading && capacityResult && !capacityResult.feasible && routes.length === 0 && (
            <Box sx={{ m: 2.5, p: 3, backgroundColor: "#fff5f5", border: "1px solid #ffcdd2", borderRadius: "10px" }}>
              <Typography variant="h6" sx={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, color: "#c62828", mb: 1 }}>
                Order Cannot Be Fulfilled
              </Typography>
              <Typography sx={{ fontFamily: "'Inter', sans-serif", color: TEXT_SECONDARY, mb: 2 }}>
                {capacityResult.message}
              </Typography>
              <Box sx={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                <Box>
                  <Typography variant="caption" sx={{ color: TEXT_SECONDARY }}>Requested Qty</Typography>
                  <Typography sx={{ fontWeight: 600, fontSize: "1.2rem" }}>
                    {capacityResult.qty?.toLocaleString()} units
                  </Typography>
                </Box>
                {capacityResult.max_possible_qty != null && (
                  <Box>
                    <Typography variant="caption" sx={{ color: TEXT_SECONDARY }}>Max Available Qty</Typography>
                    <Typography sx={{ fontWeight: 600, fontSize: "1.2rem", color: "#c62828" }}>
                      {capacityResult.max_possible_qty.toLocaleString()} units
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          )}

          {/* Each route card summarizes the optimization output and can promote a route into the details drawer. */}
          {/* Route Cards — inside the outer white card */}
          {!loading && routes.length > 0 && (
            <Box
              sx={{
                px: 7,
                pb: 5,
                pt: 3,
                display: "flex",
                flexDirection: "column",
                gap: "24px",
                borderTop: "1px solid #EAEAEA",
              }}
            >
              {displayRoutes.map((route, idx) => {
                const viewModel = buildRouteCardViewModel({
                  route,
                  idx,
                  allNodeLookup,
                  panelRoute,
                  panelRouteIdx,
                  expandedRouteIds,
                });

                return (
                  <RouteCard
                    key={route.route_id || idx}
                    route={route}
                    idx={idx}
                    product={product}
                    optimization={optimization}
                    visibleRoutes={routes}
                    viewModel={viewModel}
                    onSelect={handleRouteSelectByIndex}
                    onHoverChange={setHoveredRouteId}
                    onToggleExpanded={toggleExpandedRoute}
                    onSelectRoute={handleSelectRouteCard}
                  />
                );
              })}
            </Box>
          )}

          </Box>
          {/* end outer white card */}

        </Box>
      </Box>

      {/* Detail drawer for the actively selected route, including scenario summary and risk insights. */}
      {/* ── Selected Scenario right-side panel ── */}
      {panelRoute && (
        <SelectedScenarioPanel
          panelRoute={panelRoute}
          panelRouteIdx={panelRouteIdx}
          allNodeLookup={allNodeLookup}
          optimization={optimization}
          orderDate={orderDate}
          visibleRoutes={routes}
          onOrderDateChange={setOrderDate}
          onClose={() => setPanelRoute(null)}
          expandedRiskScore={expandedRiskScore}
          onToggleExpandedRiskScore={() => setExpandedRiskScore((v) => !v)}
          panelRiskBreakdown={panelRiskBreakdown}
          panelRiskBreakdownLoading={panelRiskBreakdownLoading}
          panelRecommendations={panelRecommendations}
          panelRecommendationsLoading={panelRecommendationsLoading}
          showRiskGraph={showRiskGraph}
          onToggleRiskView={onToggleRiskView}
          expandedTariffBreakdown={expandedTariffBreakdown}
          onToggleExpandedTariffBreakdown={() => setExpandedTariffBreakdown((prev) => !prev)}
        />
      )}
    </Box>
  );
};

export default RouteAnalyzer;

